// The authenticated identity, and the three states the app can be in.
//
// The identity is part of the WIRE contract — `SessionInfo` in `@crm/shared`,
// what `GET /api/auth/session` returns — so the UI types against the contract
// like every other screen rather than mirroring a server-side type. Cookie
// session (this app, translated to a bearer by crm-bff) and service-account
// bearer (an agent, the MCP server) converge on that one shape, which is why
// the header can render either without knowing which one it got.

import type { SessionInfo } from "@crm/shared";

/** The signed-in caller, exactly as the API reports them. */
export type Principal = SessionInfo;

export const CRM_ROLES = ["crm_reader", "crm_editor", "crm_admin"] as const;
export type CrmRole = (typeof CRM_ROLES)[number];

export function principalHasRole(principal: Principal, role: CrmRole): boolean {
  return principal.roles.includes(role);
}

/**
 * What the provider exposes. A discriminated union rather than
 * `{ principal, isLoading }` because "we don't know yet" and "nobody is signed
 * in" demand different screens, and a nullable principal lets a component
 * forget to distinguish them — which is how a signed-out user gets a flash of
 * an empty dashboard instead of a sign-in prompt.
 */
/**
 * WHY nobody is signed in. Both variants render as signed-out, but only one of
 * them is a login problem: `unauthenticated` is the API saying 401, which
 * sending the browser to Keycloak can fix, while `unreachable` is the session
 * endpoint failing to answer at all — and `/api/auth/session/login` is served by that
 * same API, so redirecting into it would bounce a dead service in a loop with
 * nothing on screen to explain why.
 */
export type AnonymousCause = "unauthenticated" | "unreachable";

export type AuthState =
  | { status: "loading" }
  | { status: "anonymous"; cause: AnonymousCause; reason: string; signInUrl: string }
  | { status: "authenticated"; principal: Principal };

/** Handy in tests and harnesses: the authenticated state for a given identity. */
export function authenticated(principal: Principal): AuthState {
  return { status: "authenticated", principal };
}
